import { CircularProgress, Tooltip } from '@mui/material';

import { Label, Switch } from '@/components/TailwindComponents';
import {
  INotificationPreferences,
  ICaseNotificationPreferences,
  ITaskNotificationPreferences,
  ICalendarEventNotificationPreferences,
} from '@/models';

interface NotificationSettingsProps {
  type: 'case' | 'task' | 'calendar-event';
  notificationPreferences: INotificationPreferences | null;
  isLoadingPreferences: boolean;
  onNotificationPreferenceChange: (
    type: 'case' | 'task' | 'calendar-event',
    key:
      | keyof ICaseNotificationPreferences
      | keyof ITaskNotificationPreferences
      | keyof ICalendarEventNotificationPreferences,
    valueOrChannel: boolean | string,
    channelValue?: boolean,
  ) => void;
}

export const NotificationSettings = ({
  type,
  notificationPreferences,
  isLoadingPreferences,
  onNotificationPreferenceChange,
}: NotificationSettingsProps) => {
  if (isLoadingPreferences) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <CircularProgress size={40} />
      </div>
    );
  }

  if (!notificationPreferences) {
    return (
      <div className="flex items-center justify-center h-[200px] text-center">
        <Label className="text-base text-gray-500 dark:text-gray-400">
          Failed to load notification preferences
        </Label>
      </div>
    );
  }

  const caseNotifications = [
    {
      key: 'newCaseAssigned' as keyof ICaseNotificationPreferences,
      title: 'A new case has been assigned to you.',
      description: 'Whenever a new case is assigned to a user.',
      mandatory: true,
    },
    {
      key: 'mentionedInCase' as keyof ICaseNotificationPreferences,
      title: "You've been mentioned in a case.",
      description: 'Whenever a user is mentioned in a case.',
      mandatory: false,
    },
    {
      key: 'caseApproved' as keyof ICaseNotificationPreferences,
      title: 'Your case has been approved.',
      description:
        'Whenever a case where the user is the primary officer is approved.',
      mandatory: false,
    },
    {
      key: 'caseReassigned' as keyof ICaseNotificationPreferences,
      title: 'Your case has been reassigned.',
      description:
        'Whenever a case where the user is the primary officer is reassigned.',
      mandatory: false,
    },
    {
      key: 'caseRejected' as keyof ICaseNotificationPreferences,
      title: 'Your case has been rejected.',
      description:
        'Whenever a case where the user is the primary officer is rejected.',
      mandatory: false,
    },
    {
      key: 'caseReopened' as keyof ICaseNotificationPreferences,
      title: 'Your case has been reopened.',
      description:
        'Whenever a case where the user is the primary officer is reopened.',
      mandatory: false,
    },
    {
      key: 'caseUpdatedByOther' as keyof ICaseNotificationPreferences,
      title: 'A case assigned to you has been updated by another user.',
      description:
        'Whenever a case assigned to a user has been updated by another user.',
      mandatory: false,
    },
  ];

  const taskNotifications = [
    {
      key: 'taskAssigned' as keyof ITaskNotificationPreferences,
      title: 'A task has been assigned to you.',
      description: 'Whenever a new task is assigned to a user.',
      mandatory: false,
      supportedChannels: ['email', 'inApp'] as const,
    },
    {
      key: 'taskMarkedComplete' as keyof ITaskNotificationPreferences,
      title: 'A task assigned to you has been completed.',
      description: 'Whenever a task assigned to a user has been completed.',
      mandatory: false,
      supportedChannels: ['email', 'inApp'] as const,
    },
    {
      key: 'taskUpdated' as keyof ITaskNotificationPreferences,
      title: 'All task changes assigned to you.',
      description:
        'Whenever any changes are made to tasks where the user is the assignee.',
      mandatory: false,
      supportedChannels: ['email', 'inApp'] as const,
    },
    {
      key: 'taskMentioned' as keyof ITaskNotificationPreferences,
      title: 'You have been mentioned in a task.',
      description: 'Whenever a user mentions you in a task comment.',
      mandatory: false,
      supportedChannels: ['email', 'inApp'] as const,
    },
  ];

  const calendarEventNotifications = [
    {
      key: 'eventAssigned' as keyof ICalendarEventNotificationPreferences,
      title: 'An Event has been assigned to you.',
      description: 'Whenever an event is assigned to a user.',
      mandatory: true, // In-App is mandatory, but email is optional
      supportedChannels: ['email', 'inApp'] as const,
    },
    {
      key: 'eventUpdated' as keyof ICalendarEventNotificationPreferences,
      title: 'An Event assigned to you has been updated.',
      description: 'Whenever an assigned event is modified.',
      mandatory: false,
      supportedChannels: ['inApp'] as const, // Only In-App supported
    },
    {
      key: 'eventResponse' as keyof ICalendarEventNotificationPreferences,
      title: 'Someone responded to your event.',
      description:
        'Whenever someone accepts or declines an event created by user.',
      mandatory: false,
      supportedChannels: ['inApp'] as const, // Only In-App supported
    },
    {
      key: 'eventReminder' as keyof ICalendarEventNotificationPreferences,
      title: 'Event reminders.',
      description:
        'Receive scheduled reminder from events user is assigned to.',
      mandatory: false,
      supportedChannels: ['email', 'inApp'] as const,
    },
    {
      key: 'eventApproval' as keyof ICalendarEventNotificationPreferences,
      title: 'Event approval requests and decisions.',
      description:
        'Whenever someone requests approval to accept an event, or your own request is approved or declined.',
      mandatory: false,
      supportedChannels: ['inApp'] as const, // Only In-App supported
    },
  ];

  let notifications = null;
  switch (type) {
    case 'task':
      notifications = taskNotifications;
      break;
    case 'calendar-event':
      notifications = calendarEventNotifications;
      break;
    case 'case':
    default:
      notifications = caseNotifications;
  }

  const getPreferenceValue = (key: string): boolean | any => {
    if (type === 'case') {
      return (
        notificationPreferences.caseNotifications?.[
          key as keyof ICaseNotificationPreferences
        ] ?? false
      );
    } else if (type === 'calendar-event' || type === 'task') {
      // For calendar events and tasks, return the channel preferences object
      return (
        (type === 'calendar-event'
          ? notificationPreferences.calendarEventNotifications?.[
              key as keyof ICalendarEventNotificationPreferences
            ]
          : notificationPreferences.taskNotifications?.[
              key as keyof ITaskNotificationPreferences
            ]) ?? { inApp: true, email: true }
      );
    }

    return false;
  };

  return (
    <div className="border-0 p-1 h-full flex flex-col">
      <div className="flex-1 overflow-auto pr-1">
        <div className="flex flex-col gap-2">
          {notifications.map((notification) => {
            const preferenceValue = getPreferenceValue(notification.key);
            // For case/task: preferenceValue is boolean
            // For calendar-event: preferenceValue is IEventChannelPreferences object
            const isChannelBased = type === 'calendar-event' || type === 'task';
            const isChecked = isChannelBased
              ? (preferenceValue as any)?.inApp ?? true
              : (preferenceValue as boolean);

            return (
              <div
                key={notification.key}
                className="flex justify-between items-center py-3 px-3 border border-gray-200 dark:border-gray-700 rounded bg-white dark:bg-gray-800 min-h-[80px]"
              >
                <div className="flex-1 mr-3">
                  <Label className="text-base font-medium mb-0.5 block">
                    {notification.title}
                  </Label>
                  <Label className="text-sm text-gray-500 dark:text-gray-400 font-normal">
                    {notification.description}
                  </Label>
                </div>
                <div className="flex items-center gap-2 min-w-[140px]">
                  {!isChannelBased ? (
                    <>
                      <Label className="text-base text-gray-500 dark:text-gray-400 font-medium">
                        In-App
                      </Label>
                      {notification.mandatory ? (
                        <Tooltip title="This cannot be turned off" arrow>
                          <span>
                            <Switch
                              size="md"
                              checked={isChecked}
                              onCheckedChange={(checked) =>
                                onNotificationPreferenceChange(
                                  type,
                                  notification.key,
                                  checked,
                                )
                              }
                              disabled={notification.mandatory}
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        <Switch
                          size="md"
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            onNotificationPreferenceChange(
                              type,
                              notification.key,
                              checked,
                            )
                          }
                          disabled={notification.mandatory}
                        />
                      )}
                    </>
                  ) : (
                    <div className="flex w-full justify-end">
                      <div className="flex border rounded-[6px] items-center">
                        {/* Email - only show if supported */}
                        {(notification as any).supportedChannels?.includes(
                          'email',
                        ) && (
                          <div className="flex items-center gap-1 py-2 px-3">
                            <Label>Email</Label>
                            <Switch
                              checked={(preferenceValue as any)?.email ?? true}
                              onCheckedChange={(checked) =>
                                onNotificationPreferenceChange(
                                  type,
                                  notification.key,
                                  'email',
                                  checked,
                                )
                              }
                            />
                          </div>
                        )}
                        {/* In-App */}
                        <div
                          className={`flex items-center gap-1 py-2 px-3 ${
                            (notification as any).supportedChannels?.includes(
                              'email',
                            )
                              ? 'border-l'
                              : ''
                          }`}
                        >
                          <Label>In-App</Label>
                          {notification.mandatory ? (
                            <Tooltip title="This cannot be turned off" arrow>
                              <span>
                                <Switch
                                  checked={
                                    (preferenceValue as any)?.inApp ?? true
                                  }
                                  onCheckedChange={(checked) =>
                                    onNotificationPreferenceChange(
                                      type,
                                      notification.key,
                                      'inApp',
                                      checked,
                                    )
                                  }
                                  disabled={notification.mandatory}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <Switch
                              checked={(preferenceValue as any)?.inApp ?? true}
                              onCheckedChange={(checked) =>
                                onNotificationPreferenceChange(
                                  type,
                                  notification.key,
                                  'inApp',
                                  checked,
                                )
                              }
                              disabled={false}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
